import * as XLSX from "xlsx"
import { convertMinutesTo12HHMM, timeStringToMinutes } from "../../../utils/generalUtils"
import { getTimeForTable, endTimeAllCases } from "../../../utils/realTimeUtils"

/**
 * Handles exporting filtered data to Excel
 * @param {Array} filteredData - The filtered patient data to export
 * @param {Array} nurseSchedule - The nurse schedule data
 * @param {Array} chairSchedule - The chair schedule data
 */
export const handleExport = (filteredData, nurseSchedule, chairSchedule) => {
    // Prepare the data for export
    const exportData = filteredData.map(item => ({
        "Chair Name":
            chairSchedule.find(chair => chair.chairId === item.assignedChair)?.chairName ||
            "Unknown Chair",
        "Nurse Name":
            nurseSchedule.find(n => n.nurseId === item.assignedNurse)?.nurseName ||
            "Unknown Nurse",
        "Patient Name": item.patientName,
        "Scheduled Start Time": convertMinutesTo12HHMM(item.scheduledStartTime),
        "Actual Start Time": item.actualStartTime
            ? convertMinutesTo12HHMM(item.actualStartTime)
            : null,
        "Scheduled End Time": convertMinutesTo12HHMM(item.scheduledEndTime),
        "Actual End Time": item.actualEndTime
            ? convertMinutesTo12HHMM(item.actualEndTime)
            : null,
        "Infusion Length (hrs)": Math.floor(item.length / 60),
        "Infusion Type": item.infusionType,
        "Reason for Visit": item.reasonForVisit,
        "Referring Department": item.department,
        "Acuity": item.acuity,
        "Clinic Appointment Start Time": item.clinicAppointmentTime
            ? convertMinutesTo12HHMM(item.clinicAppointmentTime)
            : null,
        "Arrival Time": item.arrivalTime
            ? convertMinutesTo12HHMM(item.arrivalTime)
            : null,
        "Booked Date": item.bookedDate,
        "Visit Status": item.internalStatus,
        "Clinic Provider": item.clinicProvider,
        "Clinic Appointment Type": item.linkedClinicAppointment,
        "Notes": item.notes
    }))

    // Convert the data to a worksheet
    const worksheet = XLSX.utils.json_to_sheet(exportData)

    // Create a new workbook and append the worksheet
    const workbook = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(workbook, worksheet, "Schedule")

    // Export the workbook to an Excel file
    XLSX.writeFile(workbook, "schedule_data.xlsx")
}

/**
 * Handles filter change events
 * @param {Object} e - The event object
 * @param {Function} setFilters - State setter function for filters
 */
export const handleFilterChange = (e, setFilters) => {
    const { name, value } = e.target
    if (name === "nurse") {
        const selectedOptions = Array.from(e.target.selectedOptions).map(option => option.value)
        setFilters(prev => ({ ...prev, [name]: selectedOptions }))
    } else {
        setFilters(prev => ({ ...prev, [name]: value }))
    }
}

/**
 * Filters and sorts patient data based on various criteria
 * @param {Array} data - The source data to filter and sort
 * @param {string} query - Search query string
 * @param {Object} filters - Filter criteria
 * @param {Array} nurseSchedule - Nurse schedule data
 * @param {Object} sortConfig - Sorting configuration
 * @param {Object} options - Additional options for nurse view
 * @returns {Array} Filtered and sorted data
 */
export const filterAndSortData = (
    data,
    query,
    filters,
    nurseSchedule,
    sortConfig,
    options = {}
) => {
    const { nurseId, sandbox, isTomorrowMode, confirmedCancellationNoShow, confirmedCompletedAllCases } = options

    let filteredData = [...data]

    // Filter by search query
    if (query) {
        filteredData = filteredData.filter(item =>
            item.patientName.toLowerCase().includes(query.toLowerCase())
        )
    }

    // Filter by nurse ID if specified (for nurse view)
    if (nurseId !== undefined) {
        filteredData = filteredData.filter(
            item => item.assignedNurse === nurseId || item.transferredBy === nurseId
        )
    }

    // Apply filters
    if (filters.nurse && filters.nurse.length > 0) {
        filteredData = filteredData.filter(item => {
            const nurse = nurseSchedule.find(n => n.nurseId === item.assignedNurse || n.nurseId === item.transferredBy)
            return nurse && filters.nurse.includes(nurse.nurseName)
        })
    }

    if (filters.acuity && filters.acuity.length > 0) {
        filteredData = filteredData.filter(item => filters.acuity.includes(item.acuity))
    }

    if (filters.chair && filters.chair.length > 0) {
        filteredData = filteredData.filter(item => filters.chair.includes(item.assignedChair))
    }

    if (filters.startTime !== "" || filters.endTime !== "") {
        const filterStart = filters.startTime ? timeStringToMinutes(filters.startTime) : 0
        const filterEnd = filters.endTime ? timeStringToMinutes(filters.endTime) : 1440
        filteredData = filteredData.filter(item => {
            const itemStart = getTimeForTable(item)
            return itemStart >= filterStart && itemStart <= filterEnd
        })
    }

    if (filters.lengthRange && filters.lengthRange.length === 2) {
        const [minHours, maxHours] = filters.lengthRange
        const minMinutes = minHours * 60
        const maxMinutes = maxHours * 60
        filteredData = filteredData.filter(item => item.length >= minMinutes && item.length <= maxMinutes)
    }

    if (filters.infusionType && filters.infusionType.length > 0) {
        filteredData = filteredData.filter(item => filters.infusionType.includes(item.infusionType))
    }

    if (filters.reasonForVisit && filters.reasonForVisit.length > 0) {
        filteredData = filteredData.filter(item => filters.reasonForVisit.includes(item.reasonForVisit))
    }

    if (filters.referringDepartment && filters.referringDepartment.length > 0) {
        filteredData = filteredData.filter(item => filters.referringDepartment.includes(item.department))
    }

    if (filters.patientStatus && filters.patientStatus.length > 0) {
        filteredData = filteredData.filter(item => filters.patientStatus.includes(item.internalStatus))
    }

    // Sort data
    if (sortConfig && sortConfig.key) {
        filteredData = filteredData.slice().sort((a, b) => {
            let aValue, bValue
            if (sortConfig.key === "nurseName") {
                const nurseA = nurseSchedule.find(n => n.nurseId === a.assignedNurse || n.nurseId === a.transferredBy)
                const nurseB = nurseSchedule.find(n => n.nurseId === b.assignedNurse || n.nurseId === b.transferredBy)
                aValue = nurseA ? nurseA.nurseName.toLowerCase() : ""
                bValue = nurseB ? nurseB.nurseName.toLowerCase() : ""
            } else if (sortConfig.key === "referringDepartment") {
                aValue = (a.department || "").trim().toLowerCase()
                bValue = (b.department || "").trim().toLowerCase()
            } else if (sortConfig.key === "actualStartTime") {
                aValue = getTimeForTable(a)
                bValue = getTimeForTable(b)
            } else if (sortConfig.key === "actualEndTime") {
                aValue = endTimeAllCases(a, sandbox, isTomorrowMode)
                bValue = endTimeAllCases(b, sandbox, isTomorrowMode)
            } else {
                aValue = a[sortConfig.key]
                bValue = b[sortConfig.key]
            }

            if (aValue < bValue) {
                return sortConfig.direction === "asc" ? -1 : 1
            }
            if (aValue > bValue) {
                return sortConfig.direction === "asc" ? 1 : -1
            }
            return 0
        })
    }

    // Special filter for unassigned patients (nurse view)
    if (nurseId === null && confirmedCancellationNoShow && confirmedCompletedAllCases) {
        filteredData = filteredData.filter(
            patient =>
                !patient.assignedNurse &&
                !confirmedCancellationNoShow(patient) &&
                !confirmedCompletedAllCases(patient, sandbox, isTomorrowMode)
        )
    }

    return filteredData
}

/**
 * Handles column preference changes
 * @param {Array} columns - The updated column selections
 * @param {Function} setVisibleColumns - State setter for visible columns
 * @param {string} storageKey - Key to use for localStorage
 * @param {Function} getAccessTokenSilently - Auth0 function to get token (optional)
 * @param {Function} logging - Logging utility (optional)
 * @param {Object} userMetadata - Current user metadata from Redux (optional)
 * @param {Function} dispatch - Redux dispatch function (optional)
 * @param {Function} setViewColumnAction - Redux action creator for the specific view (optional)
 */
export const handleColumnsChange = async (
    columns, 
    setVisibleColumns, 
    storageKey, 
    getAccessTokenSilently, 
    logging, 
    userMetadata = {},
    dispatch = null,
    setViewColumnAction = null
) => {
    // Update local state
    setVisibleColumns(columns)
    
    // Update Redux state if dispatch is provided
    if (dispatch && setViewColumnAction) {
        dispatch(setViewColumnAction(columns));
    }
    
    // Save to localStorage for fallback
    localStorage.setItem(storageKey, JSON.stringify(columns))
    
    // Save to Auth0 user metadata if possible
    if (getAccessTokenSilently) {
        try {
            const token = await getAccessTokenSilently();
            
            // Extract view type from storage key (chairListViewColumns → chairListView)
            const viewType = storageKey.replace('Columns', '');
            
            // Create updated metadata using Redux state
            const updatedMetadata = {
                ...userMetadata,
                [viewType]: {
                    ...(userMetadata[viewType] || {}),
                    columns: columns
                }
            };
            
            // Send updated metadata
            const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/user`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify({
                    user_metadata: updatedMetadata
                })
            });
            
            if (!response.ok) {
                throw new Error("Failed to update user metadata for columns");
            }
        } catch (error) {
            if (logging) {
                logging.error("Error updating user columns metadata:", {
                    error: error.message,
                    stack: error.stack
                });
            } else {
                console.error("Error updating user columns metadata:", error);
            }
        }
    }
}

/**
 * Loads column preferences from Redux or localStorage
 * @param {Function} setVisibleColumns - State setter for visible columns
 * @param {string} storageKey - Key to use for localStorage
 * @param {Function} getAccessTokenSilently - Auth0 function to get token (optional)
 * @param {Function} logging - Logging utility (optional)
 * @param {Object} reduxColumns - Columns from Redux state (optional)
 */
export const loadSavedColumns = async (
    setVisibleColumns, 
    storageKey, 
    getAccessTokenSilently, 
    logging,
    reduxColumns = null
) => {
    // First try to get from Redux if available
    if (reduxColumns) {
        setVisibleColumns(reduxColumns);
        return;
    }
    
    // Otherwise try localStorage for immediate display
    const savedColumns = localStorage.getItem(storageKey)
    if (savedColumns) {
        try {
            setVisibleColumns(JSON.parse(savedColumns));
        } catch (error) {
            console.error("Error loading saved columns from localStorage:", error);
        }
    }
}

/**
 * Saves filter preferences to Redux, localStorage and Auth0 user metadata
 * @param {Object} filters - Basic filter settings
 * @param {Array} activeFilters - Advanced filter settings
 * @param {string} filterRelationship - Relationship between filters (AND/OR)
 * @param {string} storageKey - Key to use for localStorage
 * @param {Function} getAccessTokenSilently - Auth0 function to get token (optional)
 * @param {Function} logging - Logging utility (optional)
 * @param {Object} userMetadata - Current user metadata from Redux (optional)
 * @param {Function} dispatch - Redux dispatch function (optional)
 * @param {Function} setViewFiltersAction - Redux action creator for the specific view (optional)
 */
export const saveFilters = async (
    filters, 
    activeFilters, 
    filterRelationship, 
    storageKey, 
    getAccessTokenSilently, 
    logging, 
    userMetadata = {},
    dispatch = null,
    setViewFiltersAction = null
) => {
    const filterData = {
        basicFilters: filters,
        advancedFilters: activeFilters,
        relationship: filterRelationship
    }
    
    // Update Redux state if dispatch is provided
    if (dispatch && setViewFiltersAction) {
        dispatch(setViewFiltersAction(filterData));
    }
    
    // Save to localStorage for fallback
    localStorage.setItem(storageKey, JSON.stringify(filterData))
    
    // Save to Auth0 user metadata if possible
    if (getAccessTokenSilently) {
        try {
            const token = await getAccessTokenSilently();
            
            // Extract view type from storage key (chairListViewFilters → chairListView)
            const viewType = storageKey.replace('Filters', '');
            
            // Create updated metadata using Redux state
            const updatedMetadata = {
                ...userMetadata,
                [viewType]: {
                    ...(userMetadata[viewType] || {}),
                    filters: filterData
                }
            };
            
            // Send updated metadata
            const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/user`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify({
                    user_metadata: updatedMetadata
                })
            });
            
            if (!response.ok) {
                throw new Error("Failed to update user metadata for filters");
            }
        } catch (error) {
            if (logging) {
                logging.error("Error updating user filters metadata:", {
                    error: error.message,
                    stack: error.stack
                });
            } else {
                console.error("Error updating user filters metadata:", error);
            }
        }
    }
}

/**
 * Loads filter preferences from Redux or localStorage
 * @param {Function} setFilters - State setter for basic filters
 * @param {Function} setActiveFilters - State setter for advanced filters
 * @param {Function} setFilterRelationship - State setter for filter relationship
 * @param {string} storageKey - Key to use for localStorage
 * @param {Function} getAccessTokenSilently - Auth0 function to get token (optional)
 * @param {Function} logging - Logging utility (optional)
 * @param {Object} reduxFilters - Filters from Redux state (optional)
 */
export const loadSavedFilters = async (
    setFilters, 
    setActiveFilters, 
    setFilterRelationship, 
    storageKey, 
    getAccessTokenSilently, 
    logging,
    reduxFilters = null
) => {
    // First try to get from Redux if available
    if (reduxFilters) {
        if (reduxFilters.basicFilters) setFilters(reduxFilters.basicFilters);
        if (reduxFilters.advancedFilters) setActiveFilters(reduxFilters.advancedFilters);
        if (reduxFilters.relationship) setFilterRelationship(reduxFilters.relationship);
        return;
    }
    
    // Otherwise try localStorage for immediate display
    const savedFilters = localStorage.getItem(storageKey)
    if (savedFilters) {
        try {
            const filterData = JSON.parse(savedFilters)
            if (filterData.basicFilters) setFilters(filterData.basicFilters)
            if (filterData.advancedFilters) setActiveFilters(filterData.advancedFilters)
            if (filterData.relationship) setFilterRelationship(filterData.relationship)
        } catch (error) {
            console.error("Error loading saved filters from localStorage:", error)
        }
    }
}

/**
 * Saves sort configuration to Redux, localStorage and Auth0 user metadata
 * @param {Object} sortConfig - Sort configuration with key and direction
 * @param {string} storageKey - Key to use for localStorage
 * @param {Function} getAccessTokenSilently - Auth0 function to get token (optional)
 * @param {Function} logging - Logging utility (optional)
 * @param {Object} userMetadata - Current user metadata from Redux (optional)
 * @param {Function} dispatch - Redux dispatch function (optional)
 * @param {Function} setViewSortConfigAction - Redux action creator for the specific view (optional)
 */
export const saveSortConfig = async (
    sortConfig, 
    storageKey, 
    getAccessTokenSilently, 
    logging, 
    userMetadata = {},
    dispatch = null,
    setViewSortConfigAction = null
) => {
    // Update Redux state if dispatch is provided
    if (dispatch && setViewSortConfigAction) {
        dispatch(setViewSortConfigAction(sortConfig));
    }
    
    // Save to localStorage for fallback
    localStorage.setItem(storageKey, JSON.stringify(sortConfig))
    
    // Save to Auth0 user metadata if possible
    if (getAccessTokenSilently) {
        try {
            const token = await getAccessTokenSilently();
            
            // Extract view type from storage key (chairListViewSortConfig → chairListView)
            const viewType = storageKey.replace('SortConfig', '');
            
            // Create updated metadata using Redux state
            const updatedMetadata = {
                ...userMetadata,
                [viewType]: {
                    ...(userMetadata[viewType] || {}),
                    sortConfig: sortConfig
                }
            };
            
            // Send updated metadata
            const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/user`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify({
                    user_metadata: updatedMetadata
                })
            });
            
            if (!response.ok) {
                throw new Error("Failed to update user metadata for sort config");
            }
        } catch (error) {
            if (logging) {
                logging.error("Error updating user sort config metadata:", {
                    error: error.message,
                    stack: error.stack
                });
            } else {
                console.error("Error updating user sort config metadata:", error);
            }
        }
    }
}

/**
 * Loads sort configuration from Redux or localStorage
 * @param {Function} setSortConfig - State setter for sort configuration
 * @param {string} storageKey - Key to use for localStorage
 * @param {Function} getAccessTokenSilently - Auth0 function to get token (optional)
 * @param {Function} logging - Logging utility (optional)
 * @param {Object} reduxSortConfig - Sort config from Redux state (optional)
 */
export const loadSavedSortConfig = async (
    setSortConfig, 
    storageKey, 
    getAccessTokenSilently, 
    logging,
    reduxSortConfig = null
) => {
    // First try to get from Redux if available
    if (reduxSortConfig) {
        setSortConfig(reduxSortConfig);
        return;
    }
    
    // Otherwise try localStorage for immediate display
    const savedConfig = localStorage.getItem(storageKey)
    if (savedConfig) {
        try {
            setSortConfig(JSON.parse(savedConfig))
        } catch (error) {
            console.error("Error loading saved sort config from localStorage:", error)
        }
    }
}

/**
 * Handles search input changes
 * @param {Object} e - The event object
 * @param {Function} setQuery - State setter for the query
 * @param {Function} filterFunction - Function to call for filtering
 * @param {Function} dispatch - Redux dispatch function (optional)
 * @param {Function} setSearchQueryAction - Redux action creator for setting search query (optional)
 */
export const handleSearch = (e, setQuery, filterFunction, dispatch = null, setSearchQueryAction = null) => {
    const query = e.target.value.toLowerCase()
    setQuery(query)
    filterFunction(query)
    
    // Update Redux state if dispatch and action creator are provided
    if (dispatch && setSearchQueryAction) {
        dispatch(setSearchQueryAction(query));
    }
}

/**
 * Loads search query from Redux state
 * @param {Function} setQuery - State setter for the query
 * @param {string} reduxSearchQuery - Search query from Redux state (optional)
 */
export const loadSavedSearchQuery = (setQuery, reduxSearchQuery = null) => {
    // Set from Redux if available
    if (reduxSearchQuery !== null) {
        setQuery(reduxSearchQuery);
    }
}

/**
 * Updates the relationship between filters (AND/OR)
 * @param {string} relationship - The relationship between filters ('AND' or 'OR')
 * @param {Function} setFilterRelationship - State setter for filter relationship
 * @param {string} storageKey - Key to use for localStorage
 * @param {Function} getAccessTokenSilently - Auth0 function to get token (optional)
 * @param {Function} logging - Logging utility (optional)
 * @param {Object} userMetadata - Current user metadata from Redux (optional)
 * @param {Function} dispatch - Redux dispatch function (optional)
 * @param {Function} setViewFiltersAction - Redux action creator for the specific view (optional)
 * @param {Object} currentFilters - Current filter state (optional)
 * @param {Array} currentActiveFilters - Current active filters (optional)
 */
export const updateFilterRelationship = async (
    relationship,
    setFilterRelationship,
    storageKey,
    getAccessTokenSilently,
    logging,
    userMetadata = {},
    dispatch = null,
    setViewFiltersAction = null,
    currentFilters = {},
    currentActiveFilters = []
) => {
    // Update local state
    setFilterRelationship(relationship)
    
    // If we have Redux integration, save all filter data
    if (dispatch && setViewFiltersAction) {
        // First save the updated relationship to all filters
        await saveFilters(
            currentFilters,
            currentActiveFilters,
            relationship,
            storageKey,
            getAccessTokenSilently,
            logging,
            userMetadata,
            dispatch,
            setViewFiltersAction
        )
    } else {
        // Otherwise just update the relationship in localStorage
        try {
            const savedFilters = localStorage.getItem(storageKey)
            if (savedFilters) {
                const filterData = JSON.parse(savedFilters)
                filterData.relationship = relationship
                localStorage.setItem(storageKey, JSON.stringify(filterData))
            }
        } catch (error) {
            console.error("Error updating filter relationship in localStorage:", error)
        }
    }
}

/**
 * Handles adding a new advanced filter
 * @param {Object} filter - The filter to add
 * @param {Array} activeFilters - Current active filters
 * @param {Function} setActiveFilters - State setter for active filters
 * @param {string} storageKey - Key to use for localStorage
 * @param {Function} getAccessTokenSilently - Auth0 function to get token (optional)
 * @param {Function} logging - Logging utility (optional)
 * @param {Object} userMetadata - Current user metadata from Redux (optional)
 * @param {Function} dispatch - Redux dispatch function (optional)
 * @param {Function} setViewFiltersAction - Redux action creator for the specific view (optional)
 * @param {Object} currentFilters - Current basic filters (optional)
 * @param {string} filterRelationship - Current filter relationship (optional)
 */
export const addAdvancedFilter = async (
    filter,
    activeFilters,
    setActiveFilters,
    storageKey,
    getAccessTokenSilently,
    logging,
    userMetadata = {},
    dispatch = null,
    setViewFiltersAction = null,
    currentFilters = {},
    filterRelationship = 'AND'
) => {
    // Create new array with the added filter
    const updatedFilters = [...activeFilters, filter]
    
    // Update local state
    setActiveFilters(updatedFilters)
    
    // If we have Redux integration, save all filter data
    if (dispatch && setViewFiltersAction) {
        // Save the updated filters
        await saveFilters(
            currentFilters,
            updatedFilters,
            filterRelationship,
            storageKey,
            getAccessTokenSilently,
            logging,
            userMetadata,
            dispatch,
            setViewFiltersAction
        )
    } else {
        // Otherwise just update the filters in localStorage
        try {
            const savedFilters = localStorage.getItem(storageKey)
            if (savedFilters) {
                const filterData = JSON.parse(savedFilters)
                filterData.advancedFilters = updatedFilters
                localStorage.setItem(storageKey, JSON.stringify(filterData))
            }
        } catch (error) {
            console.error("Error updating advanced filters in localStorage:", error)
        }
    }
}

/**
 * Handles removing an advanced filter
 * @param {number} index - The index of the filter to remove
 * @param {Array} activeFilters - Current active filters
 * @param {Function} setActiveFilters - State setter for active filters
 * @param {string} storageKey - Key to use for localStorage
 * @param {Function} getAccessTokenSilently - Auth0 function to get token (optional)
 * @param {Function} logging - Logging utility (optional)
 * @param {Object} userMetadata - Current user metadata from Redux (optional)
 * @param {Function} dispatch - Redux dispatch function (optional)
 * @param {Function} setViewFiltersAction - Redux action creator for the specific view (optional)
 * @param {Object} currentFilters - Current basic filters (optional)
 * @param {string} filterRelationship - Current filter relationship (optional)
 */
export const removeAdvancedFilter = async (
    index,
    activeFilters,
    setActiveFilters,
    storageKey,
    getAccessTokenSilently,
    logging,
    userMetadata = {},
    dispatch = null,
    setViewFiltersAction = null,
    currentFilters = {},
    filterRelationship = 'AND'
) => {
    // Create new array without the removed filter
    const updatedFilters = activeFilters.filter((_, i) => i !== index)
    
    // Update local state
    setActiveFilters(updatedFilters)
    
    // If we have Redux integration, save all filter data
    if (dispatch && setViewFiltersAction) {
        // Save the updated filters
        await saveFilters(
            currentFilters,
            updatedFilters,
            filterRelationship,
            storageKey,
            getAccessTokenSilently,
            logging,
            userMetadata,
            dispatch,
            setViewFiltersAction
        )
    } else {
        // Otherwise just update the filters in localStorage
        try {
            const savedFilters = localStorage.getItem(storageKey)
            if (savedFilters) {
                const filterData = JSON.parse(savedFilters)
                filterData.advancedFilters = updatedFilters
                localStorage.setItem(storageKey, JSON.stringify(filterData))
            }
        } catch (error) {
            console.error("Error updating advanced filters in localStorage:", error)
        }
    }
}

/**
 * Updates a basic filter value with Redux integration
 * @param {string} filterName - Name of the filter to update
 * @param {*} value - New value for the filter
 * @param {Object} currentFilters - Current filter state
 * @param {Function} setFilters - State setter for filters
 * @param {string} storageKey - Key to use for localStorage
 * @param {Function} getAccessTokenSilently - Auth0 function to get token (optional)
 * @param {Function} logging - Logging utility (optional)
 * @param {Object} userMetadata - Current user metadata from Redux (optional)
 * @param {Function} dispatch - Redux dispatch function (optional)
 * @param {Function} setViewFiltersAction - Redux action creator for the specific view (optional)
 * @param {Array} currentActiveFilters - Current active filters (optional)
 * @param {string} filterRelationship - Current filter relationship (optional)
 */
export const updateBasicFilter = async (
    filterName,
    value,
    currentFilters,
    setFilters,
    storageKey,
    getAccessTokenSilently,
    logging,
    userMetadata = {},
    dispatch = null,
    setViewFiltersAction = null,
    currentActiveFilters = [],
    filterRelationship = 'AND'
) => {
    // Create updated filters object
    const updatedFilters = {
        ...currentFilters,
        [filterName]: value
    }
    
    // Update local state
    setFilters(updatedFilters)
    
    // If we have Redux integration, save all filter data
    if (dispatch && setViewFiltersAction) {
        // Save the updated filters
        await saveFilters(
            updatedFilters,
            currentActiveFilters,
            filterRelationship,
            storageKey,
            getAccessTokenSilently,
            logging,
            userMetadata,
            dispatch,
            setViewFiltersAction
        )
    } else {
        // Otherwise just update the filters in localStorage
        try {
            const savedFilters = localStorage.getItem(storageKey)
            if (savedFilters) {
                const filterData = JSON.parse(savedFilters)
                filterData.basicFilters = updatedFilters
                localStorage.setItem(storageKey, JSON.stringify(filterData))
            }
        } catch (error) {
            console.error("Error updating basic filters in localStorage:", error)
        }
    }
}

/**
 * Resets all filters to default values
 * @param {Function} setFilters - State setter for basic filters
 * @param {Function} setActiveFilters - State setter for advanced filters
 * @param {Function} setFilterRelationship - State setter for filter relationship
 * @param {Object} defaultFilters - Default filter values
 * @param {string} storageKey - Key to use for localStorage
 * @param {Function} getAccessTokenSilently - Auth0 function to get token (optional)
 * @param {Function} logging - Logging utility (optional)
 * @param {Object} userMetadata - Current user metadata from Redux (optional)
 * @param {Function} dispatch - Redux dispatch function (optional)
 * @param {Function} setViewFiltersAction - Redux action creator for the specific view (optional)
 */
export const resetAllFilters = async (
    setFilters,
    setActiveFilters,
    setFilterRelationship,
    defaultFilters,
    storageKey,
    getAccessTokenSilently,
    logging,
    userMetadata = {},
    dispatch = null,
    setViewFiltersAction = null
) => {
    // Reset local state
    setFilters(defaultFilters)
    setActiveFilters([])
    setFilterRelationship('AND')
    
    // Default filter data
    const filterData = {
        basicFilters: defaultFilters,
        advancedFilters: [],
        relationship: 'AND'
    }
    
    // Update Redux state if dispatch is provided
    if (dispatch && setViewFiltersAction) {
        dispatch(setViewFiltersAction(filterData))
    }
    
    // Save to localStorage
    localStorage.setItem(storageKey, JSON.stringify(filterData))
    
    // Save to Auth0 user metadata if possible
    if (getAccessTokenSilently) {
        try {
            const token = await getAccessTokenSilently()
            
            // Extract view type from storage key (chairListViewFilters → chairListView)
            const viewType = storageKey.replace('Filters', '')
            
            // Create updated metadata using Redux state
            const updatedMetadata = {
                ...userMetadata,
                [viewType]: {
                    ...(userMetadata[viewType] || {}),
                    filters: filterData
                }
            }
            
            // Send updated metadata
            const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/user`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify({
                    user_metadata: updatedMetadata
                })
            })
            
            if (!response.ok) {
                throw new Error("Failed to update user metadata for filters")
            }
        } catch (error) {
            if (logging) {
                logging.error("Error updating user filters metadata:", {
                    error: error.message,
                    stack: error.stack
                })
            } else {
                console.error("Error updating user filters metadata:", error)
            }
        }
    }
} 